import {addWeeks, format, startOfWeek} from 'date-fns';
import {collection, DocumentData, DocumentReference, getDocs, onSnapshot, query, QueryDocumentSnapshot, Unsubscribe, where} from 'firebase/firestore';
import {db} from '../firebaseConfig';
import {Department, DEPARTMENTS, IFirestoreEvent, IFirestoreTraveler, IProductionZone, ITraveler, TravelerType} from './interfaces';
import {normalizeProjectCrews} from './normalizeProjectCrews';
import {firestoreCollections} from './firestoreConstants';
import {PRODUCTION_TRAVELER_STATUSES} from '../utils/productionTravelerStatus';
import {normalizeReadyToShipDeadline} from './normalizeReadyToShipDeadline';

interface ScheduledTravelerEventState {
  scheduledTravelerPaths: Set<string>;
  previouslyScheduledTravelerPaths: Set<string>;
}

function isVisibleAssemblyTraveler(traveler: ITraveler): boolean {
  return (
    traveler.requiresAssembly
    && !traveler.hideFromAssemblyTaskList
    && traveler.isValidAssemblyStatus
  );
}

function travelerTypeForDepartment(department: Department): TravelerType {
  if (department === DEPARTMENTS.assembly) {
    return DEPARTMENTS.production;
  }

  return department;
}

function departmentForTravelerType(travelerType: TravelerType): Department {
  if (travelerType === DEPARTMENTS.production) {
    return DEPARTMENTS.assembly;
  }
  return travelerType;
}

function mapTravelerDoc(
  doc: QueryDocumentSnapshot<DocumentData>,
  calendarDepartment?: Department
): ITraveler {
  const travelerData = doc.data() as IFirestoreTraveler;

  return {
    id: doc.id,
    ref: doc.ref as DocumentReference,
    workOrderType: travelerData.workOrderType,
    displayName: travelerData.calendarDisplayName,
    department: calendarDepartment ?? departmentForTravelerType(travelerData.thisTravelerIsFor),
    status: travelerData.status,
    portalLink: travelerData.portalLink,
    readyToShipDeadline: normalizeReadyToShipDeadline(travelerData.readyToShipDeadline),
    fabCrew: travelerData.fabCrew,
    events: travelerData.events,
    projectCrews: normalizeProjectCrews(travelerData.projectCrews),
    requiresAssembly: travelerData.requiresAssembly === true,
    hideFromAssemblyTaskList: travelerData.hideFromAssemblyTaskList === true,
    isValidAssemblyStatus: travelerData.isValidAssemblyStatus === true,
    itemTypeString: travelerData.itemTypeString ?? null,
  };
}

async function fetchProductionTravelers(productionZone?: IProductionZone | null): Promise<ITraveler[]> {
  const fabCrewRefs = productionZone?.fabCrews ?? [];

  if (fabCrewRefs.length === 0) {
    return [];
  }

  const travelersRef = collection(db, firestoreCollections.travelers);
  const productionTravelersSnapshot = await getDocs(query(
    travelersRef,
    where('thisTravelerIsFor', '==', DEPARTMENTS.production),
    where('fabCrew', 'in', fabCrewRefs),
    where('status', 'in', PRODUCTION_TRAVELER_STATUSES)
  ));

  return productionTravelersSnapshot.docs.map((travelerDoc) => mapTravelerDoc(travelerDoc, DEPARTMENTS.production));
}

/**
 * Fetches travelers from Firestore, optionally filtering by department.
 *
 * @param {Department} [department] - (Optional) The department to filter travelers by.
 * @returns {Promise<ITraveler[]>} A promise that resolves to an array of traveler objects.
 */
export async function fetchTravelers(
  department?: Department,
  productionZone?: IProductionZone | null
): Promise<ITraveler[]> {
  try {
    if (department === DEPARTMENTS.production) {
      return await fetchProductionTravelers(productionZone);
    }

    const travelersRef = collection(db, firestoreCollections.travelers);

    const travelerQuery = department
      ? query(
        travelersRef,
        where('thisTravelerIsFor', '==', travelerTypeForDepartment(department)),
        where('status', 'not-in', ['Installed/Delivered (completely done)', 'Canceled'])
      )
      : query(
        travelersRef,
        where('status', 'not-in', ['Installed/Delivered (completely done)', 'Canceled'])
      );
    const querySnapshot = await getDocs(travelerQuery);

    // Map Firestore traveler docs to UI traveler objects.
    const travelers = querySnapshot.docs.map((travelerDoc) => mapTravelerDoc(travelerDoc));

    // Assembly applies additional visibility rules driven by traveler flags.
    if (department === DEPARTMENTS.assembly) {
      return travelers.filter(isVisibleAssemblyTraveler);
    }

    return travelers;
  } catch (error) {
    console.error('Error fetching travelers:', error);
    return [];
  }
}

function filterUnscheduledTravelers(
  docs: QueryDocumentSnapshot<DocumentData>[],
  department: Department,
  scheduledTravelerEventState: ScheduledTravelerEventState
): ITraveler[] {
  const mapTraveler = department === DEPARTMENTS.production
    ? (travelerDoc: QueryDocumentSnapshot<DocumentData>) => mapTravelerDoc(travelerDoc, DEPARTMENTS.production)
    : (travelerDoc: QueryDocumentSnapshot<DocumentData>) => mapTravelerDoc(travelerDoc);

  return docs
    .map(mapTraveler)
    .map((traveler) => ({
      ...traveler,
      wasPreviouslyScheduled: scheduledTravelerEventState.previouslyScheduledTravelerPaths.has(traveler.ref.path),
    }))
    .filter((traveler) =>
      !scheduledTravelerEventState.scheduledTravelerPaths.has(traveler.ref.path)
      && (department !== DEPARTMENTS.assembly || isVisibleAssemblyTraveler(traveler))
    );
}

function getCurrentWeekStartString(): string {
  return format(startOfWeek(new Date()), 'yyyy-MM-dd');
}

function getScheduledTravelerEventStateForEvents(
  docs: QueryDocumentSnapshot<DocumentData>[],
  currentWeekStartString: string
): ScheduledTravelerEventState {
  const scheduledTravelerPaths = new Set<string>();
  const previouslyScheduledTravelerPaths = new Set<string>();

  docs.forEach((eventDoc) => {
    const eventData = eventDoc.data() as IFirestoreEvent;
    const eventTravelerPaths = eventData.traveler?.map((travelerRef) => travelerRef.path) ?? [];

    if (eventData.endDate >= currentWeekStartString) {
      eventTravelerPaths.forEach((travelerPath) => scheduledTravelerPaths.add(travelerPath));
      return;
    }

    eventTravelerPaths.forEach((travelerPath) => previouslyScheduledTravelerPaths.add(travelerPath));
  });

  return {
    scheduledTravelerPaths,
    previouslyScheduledTravelerPaths,
  };
}

function subscribeToScheduledTravelerEventState(
  department: Department,
  onScheduledTravelerEventStateChange: (scheduledTravelerEventState: ScheduledTravelerEventState) => void,
  onError: (error: Error) => void
): Unsubscribe {
  const eventsRef = collection(db, firestoreCollections.events);
  const eventsQuery = query(eventsRef, where('department', '==', department));
  let eventDocs: QueryDocumentSnapshot<DocumentData>[] = [];
  let refreshTimeoutId: number | undefined;

  const publishEventState = () => {
    onScheduledTravelerEventStateChange(
      getScheduledTravelerEventStateForEvents(eventDocs, getCurrentWeekStartString())
    );
  };

  const scheduleWeekRefresh = () => {
    refreshTimeoutId = window.setTimeout(() => {
      publishEventState();
      scheduleWeekRefresh();
    }, addWeeks(startOfWeek(new Date()), 1).getTime() - Date.now());
  };

  const unsubscribe = onSnapshot(
    eventsQuery,
    (querySnapshot) => {
      eventDocs = querySnapshot.docs;
      publishEventState();
      window.clearTimeout(refreshTimeoutId);
      scheduleWeekRefresh();
    },
    (error) => {
      console.error('Error fetching scheduled traveler events:', error);
      onError(error);
    }
  );

  return () => {
    unsubscribe();
    window.clearTimeout(refreshTimeoutId);
  };
}

function createUnscheduledTravelerSnapshotHandler(
  department: Department,
  onDataChange: (data: ITraveler[]) => void
): {
  handleTravelersChange: (travelerDocs: QueryDocumentSnapshot<DocumentData>[]) => void;
  handleScheduledTravelerEventStateChange: (scheduledTravelerEventState: ScheduledTravelerEventState) => void;
} {
  let latestTravelerDocs: QueryDocumentSnapshot<DocumentData>[] | null = null;
  let latestScheduledTravelerEventState: ScheduledTravelerEventState | null = null;

  const publishIfReady = () => {
    if (!latestTravelerDocs || !latestScheduledTravelerEventState) {
      return;
    }

    onDataChange(filterUnscheduledTravelers(
      latestTravelerDocs,
      department,
      latestScheduledTravelerEventState
    ));
  };

  return {
    handleTravelersChange: (travelerDocs) => {
      latestTravelerDocs = travelerDocs;
      publishIfReady();
    },
    handleScheduledTravelerEventStateChange: (scheduledTravelerEventState) => {
      latestScheduledTravelerEventState = scheduledTravelerEventState;
      publishIfReady();
    },
  };
}

function fetchProductionUnscheduledTravelers(
  onDataChange: (data: ITraveler[]) => void,
  onError: (error: Error) => void,
  productionZone?: IProductionZone | null
): Unsubscribe {
  const travelersRef = collection(db, firestoreCollections.travelers);
  let unsubscribeProductionTravelers: Unsubscribe | undefined;
  let unsubscribeScheduledTravelerEventState: Unsubscribe | undefined;
  const {
    handleTravelersChange,
    handleScheduledTravelerEventStateChange,
  } = createUnscheduledTravelerSnapshotHandler(DEPARTMENTS.production, onDataChange);

  const subscribeProductionTravelers = (fabCrewRefs: DocumentReference[]) => {
    if (fabCrewRefs.length === 0) {
      onDataChange([]);
      return;
    }

    unsubscribeProductionTravelers = onSnapshot(
      query(
        travelersRef,
        where('thisTravelerIsFor', '==', DEPARTMENTS.production),
        where('fabCrew', 'in', fabCrewRefs),
        where('status', 'in', PRODUCTION_TRAVELER_STATUSES)
      ),
      (querySnapshot) => {
        handleTravelersChange(querySnapshot.docs);
      },
      (error) => {
        console.error('Error fetching unscheduled travelers:', error);
        onError(error);
      }
    );
  };

  unsubscribeScheduledTravelerEventState = subscribeToScheduledTravelerEventState(
    DEPARTMENTS.production,
    handleScheduledTravelerEventStateChange,
    onError
  );
  subscribeProductionTravelers(productionZone?.fabCrews ?? []);

  return () => {
    unsubscribeProductionTravelers?.();
    unsubscribeScheduledTravelerEventState?.();
  };
}

/**
 * Fetches unscheduled travelers from Firestore, filtering by department and current/future same-department events.
 *
 * @param {Department} department - The department to filter travelers by.
 * @param {(data: ITraveler[]) => void} onDataChange - Callback invoked with the traveler data.
 * @param {(error: Error) => void} onError - Callback invoked if there's an error.
 * @returns {Unsubscribe} A method for unsubscribing from real-time updates.
 */
export function fetchUnscheduledTravelers(
  department: Department,
  onDataChange: (data: ITraveler[]) => void,
  onError: (error: Error) => void,
  productionZone?: IProductionZone | null
): Unsubscribe {
  if (department === DEPARTMENTS.production) {
    return fetchProductionUnscheduledTravelers(onDataChange, onError, productionZone);
  }

  const travelersRef = collection(db, firestoreCollections.travelers);
  const {
    handleTravelersChange,
    handleScheduledTravelerEventStateChange,
  } = createUnscheduledTravelerSnapshotHandler(department, onDataChange);

  // Set up the real-time listener
  const unsubscribeScheduledTravelerEventState = subscribeToScheduledTravelerEventState(
    department,
    handleScheduledTravelerEventStateChange,
    onError
  );

  const unsubscribe = onSnapshot(
    query(
      travelersRef,
      where('thisTravelerIsFor', '==', travelerTypeForDepartment(department)),
      where('status', 'not-in', ['Installed/Delivered (completely done)', 'Canceled'])
    ),
    (querySnapshot) => {
      handleTravelersChange(querySnapshot.docs);
    },
    (error) => {
      console.error('Error fetching unscheduled travelers:', error);
      onError(error);
    }
  );

  return () => {
    unsubscribe();
    unsubscribeScheduledTravelerEventState();
  };
}
